import {Card, DataStatus, IconButton, openDeleteDialog, Switch} from 'platform/components';
import {Box, Center, Text} from 'platform/foundation';

import {
  InspectionType,
  useDeleteInspectionTemplateMutation,
  useGetInspectionTemplatesQuery,
  useToggleInspectionTemplateActiveStateMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

export function VehicleInspections() {
  const navigate = useNavigate();
  const {
    data: inspectionTemplates,
    isLoading,
    isError,
  } = useGetInspectionTemplatesQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: (queryResult) => {
        const filtered = queryResult.data?.filter(
          (template) =>
            // Vehicle condition has a separate page. Also it should not be possible to toggle it's active state
            template.inspectionType !== InspectionType.VEHICLE_CONDITION &&
            template.inspectionType !== InspectionType.AUTOFILL_TEMPLATE
        );
        return {...queryResult, data: filtered};
      },
    }
  );
  const [deleteInspectionTemplate] = useDeleteInspectionTemplateMutation();
  const [toggleInspectionTemplateActiveState] = useToggleInspectionTemplateActiveStateMutation();

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.vehicleSettings.labels.inspectionsTitle')}}
      description={i18n.t('page.vehicleSettings.labels.inspectionsDescription')}
      data-testid="integrations-inspections-settings-modal"
    >
      <DataStatus isEmpty={!inspectionTemplates?.length} isLoading={isLoading} isError={isError}>
        <Card
          title={i18n.t`page.vehicleSettings.labels.inspectionsTitle`}
          actions={[
            {
              type: 'button',
              variant: 'ghostLink',
              leftIcon: 'content/add_circle',
              title: i18n.t('page.vehicleSettings.actions.addNewInspection'),
              onClick: () => navigate(settingsRoutes.vehicleInspectionsNew),
            },
          ]}
        >
          {inspectionTemplates?.map((template) => (
            <Box
              key={template?.id}
              padding={3}
              borderBottom="1px solid"
              borderColor="general.separator"
            >
              <Center>
                <Switch
                  data-testid="integrations-inspections-settings-modal-switch"
                  value={template.isActive}
                  onChange={() =>
                    toggleInspectionTemplateActiveState({templateId: template.id})
                      .unwrap()
                      .catch(handleApiError)
                  }
                />
                <Box flexGrow={1} paddingLeft={4}>
                  <Text size="small">{template.name}</Text>
                </Box>
                <IconButton
                  data-testid="integrations-inspections-settings-modal-icon-edit"
                  icon="image/edit"
                  onClick={() =>
                    navigate(
                      composePath(settingsRoutes.vehicleInspectionDetail, {
                        params: {id: template.id},
                      })
                    )
                  }
                />
                <IconButton
                  data-testid="integrations-inspections-settings-modal-icon-delete"
                  icon="action/delete"
                  isDisabled={!template.isDeletable}
                  onClick={() =>
                    openDeleteDialog({
                      text: i18n.t('page.vehicleSettings.actions.deleteInspectionTemplate'),
                      onConfirm: () => {
                        deleteInspectionTemplate({templateId: template.id})
                          .unwrap()
                          .catch(handleApiError);
                      },
                    })
                  }
                />
              </Center>
            </Box>
          ))}
        </Card>
      </DataStatus>
    </SettingsTemplate>
  );
}
